/**@flow */

import http from "@http";
import type { Action } from "redux-action";
import actionCreators from "@modules/reporting/actionCreator";
import { all, call, put, takeEvery, spawn } from "redux-saga/effects";
import focusListsSaga from "./focus-lists";
import trendsSaga from "./trends";
import notificationsSaga from "./notifications";
import employeeSaga from "./employee";
import managerSaga from "./manager";
import reportsSaga from "./reports";
import firstCareIndexSaga from "./first-care-index";
import referralTrackerSaga from "./referral-tracker";
import tasksSaga from "./tasks";
import dataForecastsSaga from "./data-forecasts";
import dashboardSaga from "./dashboard";
import healthReportSaga from "./health";
import providersReportSaga from "./providers";
import {
  GetAbsenceNow,
  GetToolsDashboard,
  GetLoggedUserDashboardOverview,
  GetCoronavirusToolsDashboard,
} from "@modules/reporting/constants/actions";
import { getEffectiveAbsenceParameters } from "@modules/reporting/sagas/focus-lists/department/report";
import config from "@config";

type ReportRequest = $Exact<{
  clientId: number,
  filters?: Array<string>,
}>;

export function* onGetAbsenceNow({ payload }: Action<ReportRequest>): Generator<void, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const [response, effectiveParameters] = yield all([
      call(http.get, `/api/v1/tools/client/${payload.clientId}/tools/dashboard/absence-now`),
      call(getEffectiveAbsenceParameters, payload.clientId),
    ]);

    yield put(actionCreators.GetAbsenceNowSuccess({ ...response.data, effectiveParameters }));
  } catch (e) {
    yield put(actionCreators.GetAbsenceNowError(e.response));
  }
}

export function* onGetLoggedUserDashboardOverview({
  payload,
}: Action<ReportRequest>): Generator<void, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const response = yield call(
      http.get,
      `api/v1/tools/client/${payload.clientId}/tools/dashboard/overview`,
    );
    yield put(actionCreators.GetLoggedUserDashboardOverviewSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetLoggedUserDashboardOverviewError(e.response));
  }
}

export function* onGetToolsDashboard({ payload }: Action<ReportRequest>): Generator<void, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const response = yield call(
      http.get,
      `/api/v1/tools/client/${payload.clientId}/tools/dashboard/overview`,
    );

    yield all([
      put(actionCreators.GetToolsDashboardSuccess(response.data)),
      put(actionCreators.GetAbsenceNow({ clientId: payload.clientId })),
    ]);
  } catch (e) {
    yield put(actionCreators.GetToolsDashboardError(e.response));
  }
}

export function* onGetCoronavirusToolsDashboard({
  payload,
}: Action<ReportRequest>): Generator<void, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/tools/client/${payload.clientId}/tools/targeted-reasons`,
      {
        params: payload.filters
          ? {
              reason_ids: (payload.filters || []).join(","),
            }
          : {},
      },
    );

    yield put(actionCreators.GetCoronavirusToolsDashboardSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetCoronavirusToolsDashboardError(e.response));
  }
}

export function* ReportingSaga(): Generator<*, void, void> {
  yield spawn(focusListsSaga);
  yield spawn(trendsSaga);
  yield spawn(notificationsSaga);
  yield spawn(employeeSaga);
  yield spawn(managerSaga);
  yield spawn(reportsSaga);
  yield spawn(firstCareIndexSaga);
  yield spawn(referralTrackerSaga);
  yield spawn(tasksSaga);
  yield spawn(dataForecastsSaga);
  yield spawn(dashboardSaga);
  yield spawn(healthReportSaga);
  yield spawn(providersReportSaga);
  yield all([takeEvery(GetAbsenceNow, onGetAbsenceNow)]);
  yield all([takeEvery(GetLoggedUserDashboardOverview, onGetLoggedUserDashboardOverview)]);
  yield all([takeEvery(GetToolsDashboard, onGetToolsDashboard)]);
  yield all([takeEvery(GetCoronavirusToolsDashboard, onGetCoronavirusToolsDashboard)]);
}

export default ReportingSaga;
